import { useCallback, useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  RiLogoutCircleRLine,
  RiUser2Fill,
  RiMoneyDollarCircleFill,
  RiAddLine,
  RiNotification2Line,
  RiInformationLine,
  RiShoppingBag3Line,
  RiArrowLeftLine,
  RiKey2Line,
  RiFileList3Line,
  RiCompass3Line,
  RiAwardLine,
  RiPrinterLine,
} from "react-icons/ri";
import { LuFileSignature } from "react-icons/lu";
import { FiUsers } from "react-icons/fi";
import { HiPencilAlt } from "react-icons/hi";
import { FaWhatsapp } from "react-icons/fa";
import { MdDevices } from "react-icons/md";
import { useAuth } from "../../hooks/AuthContext";
import { i18n } from "../../localization/i18n";
import { getLicenseeDisplayName } from "../../utils/formatters";
import { differenceInMinutes, differenceInHours, differenceInDays } from "date-fns";

import {
  Container,
  Overlay,
  NavigationAction,
  TopBarOptions,
  ProfileAvatar,
  ProfileWrapper,
  ProfileMenuContainer,
  ProfileMenuHeader,
  ProfileHeaderTextGroup,
  ProfileHeaderPrimaryText,
  ProfileHeaderSecondaryText,
  ProfileMenuBody,
  ProfileMenuFooter,
  ProfileMenuButton,
  CreditsText,
  CreditsWrapper,
  LogoDesktop,
  LogoMobile,
  NotificationsWrapper,
  ButtonIcon,
  NotificationBadge,
  NotificationsDropdownContainer,
  NotificationsHeader,
  NotificationsTitle,
  MarkAllReadButton,
  NotificationsList,
  NotificationItem,
  NotificationIconWrapper,
  NotificationContent,
  NotificationItemTitle,
  NotificationItemBody,
  NotificationTime,
  EmptyNotifications,
  MobileNotificationsHeader,
  MobileHeaderLeft,
  MobileHeaderTitle,
  NotificationActionText,
} from "./styles";
import { roles } from "../../utils/constants";
import { useOnboard } from "../../hooks/OnboardContext";
import { PrinterControl } from "../PrinterControl";
import ChangePasswordPopup from "../ChangePasswordPopup";
import global from "../../global";
import { Modal } from "../Modal";
import { PersonModal } from "../PersonModal";
import api from "../../services/api";
import { IPerson } from "../PersonModal/dtos";
import { usePrinters } from "../../hooks/PrintersContext";
import { LockedFeatureIcon } from "../LockedFeatureIcon";

import NavBtn from "../NavBtn";
import Symbol from "../../assets/symbol-purple.svg";
import Logo from "../../assets/logo.svg";

import { toast } from "react-toastify";
import { onMessageListener } from "../../services/firebase";

const notificationStyles: Record<string, {
  icon: React.ReactNode;
  color: string;
  backgroundColor: string;
  route: string;
}> = {
  new_lead_patient: {
    icon: <RiUser2Fill size={20} />,
    color: '#1976d2', // Blue
    backgroundColor: '#e3f2fd',
    route: '/profile/patients',
  },
  order_created: {
    icon: <RiShoppingBag3Line size={20} />,
    color: '#f57c00', // Orange
    backgroundColor: '#fff3e0',
    route: '/orders',
  },
  order_created_urgent: {
    icon: <RiShoppingBag3Line size={20} />,
    color: '#d32f2f', // Red/Urgent
    backgroundColor: '#ffebee',
    route: '/orders',
  },
  order_update: {
    icon: <RiShoppingBag3Line size={20} />,
    color: '#388e3c', // Green
    backgroundColor: '#e8f5e9',
    route: '/orders',
  },
  system_alert: {
    icon: <RiInformationLine size={20} />,
    color: '#d32f2f', // Red
    backgroundColor: '#ffebee',
    route: '/dashboard',
  }
};

const formatCompactTime = (dateInput: string | Date, lang: string): string => {
  const date = new Date(dateInput);
  const now = new Date();
  
  const diffInMinutes = differenceInMinutes(now, date);
  const diffInHours = differenceInHours(now, date);
  const diffInDays = differenceInDays(now, date);

  const isPt = lang.startsWith("pt");
  const isEs = lang.startsWith("es");

  if (diffInMinutes < 1) {
    if (isPt) return "agora";
    if (isEs) return "ahora";
    return "now";
  }

  if (diffInMinutes < 60) {
    return `${diffInMinutes}min`;
  }

  if (diffInHours < 24) {
    return `${diffInHours}h`;
  }

  return `${diffInDays}d`;
};

const getOrderStatusStyle = (status?: string) => {
  switch (status) {
    case "completed":
      return {
        color: "var(--active-green)",
        backgroundColor: "#e8fbf1",
      };
    case "cancelled":
      return {
        color: "var(--error)",
        backgroundColor: "#ffebee",
      };
    case "development":
      return {
        color: "var(--development)",
        backgroundColor: "#e3f2fd",
      };
    case "review":
      return {
        color: "#D67D11",
        backgroundColor: "#fff9e6",
      };
    case "processing":
      return {
        color: "var(--gray)",
        backgroundColor: "#f5f5f7",
      };
    default:
      return null;
  }
};

interface TopbarProps {
  isMenuOpen: boolean;
  toggleMenu: () => void;
  extendedMode: boolean;
  setExtendedMode: (val: boolean) => void;
}

interface INotification {
  id: string;
  type: string;
  title: string;
  body: string;
  metadata: any;
  read_at: string | null;
  created_at: string;
}

const Topbar = ({ isMenuOpen, toggleMenu }: TopbarProps) => {
  const {
    signOut,
    user,
    licenseeFixcoins,
    isFixiter: authIsFixiter,
    isLicenseeWithPrinter: authIsLicenseeWithPrinter,
    hasProvider: authHasProvider,
  } = useAuth();
  const {
    licensee,
    planHasExpired,
    isSubscriptionCancelled,
    isFixiter: onboardIsFixiter,
    isLicenseeWithPrinter: onboardIsLicenseeWithPrinter,
    hasProvider: onboardHasProvider,
  } = useOnboard();
  const isLicenseeWithPrinter = onboardIsLicenseeWithPrinter || authIsLicenseeWithPrinter;
  const isFixiter = isLicenseeWithPrinter
    ? false
    : onboardIsFixiter || authIsFixiter || user?.role === roles.fixiter;
  const isFixiterForCertificate = Boolean(
    isFixiter || user?.role === roles.fixiter
  );
  const hasProvider = Boolean(
    onboardHasProvider ||
      authHasProvider ||
      (licensee as any)?.provider_id ||
      (licensee as any)?.provider?.id ||
      (user as any)?.person?.licensee?.provider_id ||
      (user as any)?.person?.licensee?.provider?.id ||
      (user as any)?.licensee?.provider_id ||
      (user as any)?.licensee?.provider?.id
  );
  const [hasCourseFromApi, setHasCourseFromApi] = useState<boolean | null>(null);
  const { userHasPrinter } = usePrinters();
  const navigate = useNavigate();
  const location = useLocation();

  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (!isFixiterForCertificate || !user?.id) {
      setHasCourseFromApi(false);
      return;
    }

    const checkLinkedFromPayload = (d: any): boolean => {
      if (!d || typeof d !== "object") return false;
      const msg = String(d.message || "").toLowerCase();
      if (
        msg.includes("nenhuma turma") ||
        msg.includes("não possui turma") ||
        msg.includes("nao possui turma") ||
        msg.includes("sem turma") ||
        msg.includes("não vinculado") ||
        msg.includes("nao vinculado") ||
        msg.includes("não está vinculado") ||
        msg.includes("nao esta vinculado") ||
        msg.includes("não encontrado") ||
        msg.includes("nao encontrado")
      ) {
        return false;
      }
      return Boolean(
        d.available === true ||
          d.course_id ||
          d.courseId ||
          d.course_name ||
          d.courseName ||
          d.course?.id ||
          d.course?.name ||
          msg.includes("treinamento ao vivo") ||
          msg.includes("após a conclusão") ||
          msg.includes("apos a conclusao") ||
          msg.includes("estará disponível") ||
          msg.includes("estara disponivel")
      );
    };

    let cancelled = false;
    api
      .get("/courses/certificate/me")
      .then((res) => {
        if (cancelled) return;
        setHasCourseFromApi(checkLinkedFromPayload(res?.data));
      })
      .catch((err) => {
        if (cancelled) return;
        setHasCourseFromApi(checkLinkedFromPayload(err?.response?.data));
      });

    return () => {
      cancelled = true;
    };
  }, [isFixiterForCertificate, user?.id, location.pathname, isProfileOpen]);

  const hasLinkedCourse = Boolean(hasCourseFromApi);
  const [isCreatePersonOpen, setisCreatePersonOpen] = useState(false);
  const [person, setPerson] = useState<IPerson>();
  const [lockedPrinterModalOpen, setLockedPrinterModalOpen] = useState(false);

  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);

  const notificationsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleNotificationsMouseEnter = () => {
    if (isMobile) return;
    if (notificationsTimeoutRef.current) {
      clearTimeout(notificationsTimeoutRef.current);
      notificationsTimeoutRef.current = null;
    }
    if (!isNotificationsOpen) {
      setIsNotificationsOpen(true);
      fetchNotifications();
    }
  };

  const handleNotificationsMouseLeave = () => {
    if (isMobile) return;
    notificationsTimeoutRef.current = setTimeout(() => {
      setIsNotificationsOpen(false);
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (notificationsTimeoutRef.current) {
        clearTimeout(notificationsTimeoutRef.current);
      }
    };
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!user?.id || isSubscriptionCancelled) return;
    try {
      const response = await api.get('/notifications/unread');
      setUnreadCount(response.data.count || 0);
    } catch (error) {
      console.error("Failed to fetch unread notifications count:", error);
    }
  }, [user?.id, isSubscriptionCancelled]);

  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!user?.id || isSubscriptionCancelled) return;
    
    const setupMessageListener = async () => {
      try {
        const payload: any = await onMessageListener();
        
        if (payload && payload.notification) {
          // Atualiza a bolinha vermelha e avisa com um Toast
          setUnreadCount((prev) => prev + 1);
          
          toast.info(
            <div>
              <strong>{payload.notification.title}</strong>
              <br/>
              <span style={{ fontSize: '13px' }}>{payload.notification.body}</span>
            </div>, 
            { 
              position: "top-right", 
              autoClose: 5000,
              onClick: () => {
                // Se estiver com o dropdown aberto, recarrega a lista
                if (isNotificationsOpen) {
                  fetchNotifications();
                } else {
                  toggleNotifications(); // Abre a gaveta e ja baixa as notifs
                }
              }
            }
          );

          // Dispara o push nativo do sistema mesmo com o app aberto para persistir na Central do OS
          if (Notification.permission === "granted") {
            try {
              new Notification(payload.notification.title, {
                body: payload.notification.body,
                icon: "/favicon.ico",
              });
            } catch (err) {
              console.warn("Failed to trigger native notification in foreground:", err);
            }
          }

          // Se a lista ja estiver aberta, vamos rebaixar os dados 
          if (isNotificationsOpen) {
             fetchNotifications();
          }
        }
        
        // Fica recursivo esperando a proxima mensagem
        setupMessageListener(); 
      } catch (err) {
        console.log('FCM Listener Error: ', err);
      }
    };

    setupMessageListener();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isNotificationsOpen]);

  async function fetchNotifications() {
    setIsLoadingNotifications(true);
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }

  async function handleNotificationClick(notificationItem: INotification) {
    if (!notificationItem.read_at) {
      try {
        await api.patch(`/notifications/${notificationItem.id}/read`);
        setUnreadCount((prev) => Math.max(0, prev - 1));
        setNotifications((prevList) => 
          prevList.map((item) => 
            item.id === notificationItem.id ? { ...item, read_at: new Date().toISOString() } : item
          )
        );
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    }

    setIsNotificationsOpen(false);

    // If it's a lead patient, open the WhatsApp link in a new tab
    if (notificationItem.type === 'new_lead_patient') {
      const whatsappLink = notificationItem.metadata?.whatsapp_link;
      if (whatsappLink) {
        window.open(whatsappLink, '_blank');
      }
      return;
    }

    // If it's an order notification, go directly to the general orders screen
    if (
      notificationItem.type === 'order_created' || 
      notificationItem.type === 'order_created_urgent' || 
      notificationItem.type === 'order_update'
    ) {
      navigate('/orders');
      return;
    }

    // Resolve style config dynamically for other fallbacks
    const styleConfig = notificationStyles[notificationItem.type] || notificationStyles.system_alert;
    let targetRoute = styleConfig.route;
    
    // Prefix the target route with the role if it's a relative path inside user profile
    if (targetRoute.startsWith('/profile/')) {
      targetRoute = `/${user.role.toLocaleLowerCase()}${targetRoute}`;
    }

    navigate(targetRoute);
  }

  async function handleMarkAllAsRead() {
    try {
      await api.patch('/notifications/read-all');
      setUnreadCount(0);
      setNotifications((prevList) => 
        prevList.map((item) => ({ ...item, read_at: item.read_at || new Date().toISOString() }))
      );
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }

  function toggleNotifications() {
    const willOpen = !isNotificationsOpen;
    setIsNotificationsOpen(willOpen);
    if (willOpen) {
      fetchNotifications();
    }
  }

  const toggleProfile = useCallback(() => {
    setIsProfileOpen((prev) => {
      const next = !prev;
      if (next) {
        setIsNotificationsOpen(false);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setIsProfileOpen(false);
  }, [location.pathname]);

  function togglePopup() {
    if (user.id) {
      setIsPopupOpen(!isPopupOpen);
    }
  }

  const insertUserIntoPerson = useCallback(async (person_id: string, user_id: string) => {
    try {
      const body = {
        person_id: person_id,
        user_id: user_id,
      };

      await api.patch(`/person/update-person`, body);
    } catch (error) {
      console.log(error);
    }
  }, []);

  useEffect(() => {
    if (!user.person && person) {
      insertUserIntoPerson(person.id, user.id);
    }
  }, [person, user.person, user.id, insertUserIntoPerson]);

  function checkDevice() {
    if (
      window.matchMedia(
        `(min-width:${global.responsive.small.width.value}${global.responsive.small.width.unit})`
      ).matches
    ) {
      setIsMobile(false);
    } else {
      setIsMobile(true);
    }
  }

  useEffect(() => {
    checkDevice();
  }, [toggleProfile]);

  const isLicenseeOrFixiter =
    user.role === roles.licensee || user.role === roles.fixiter;
  const canAccessProfileTabs =
    isLicenseeOrFixiter && !planHasExpired && !isSubscriptionCancelled;

  const profileTabOptions = canAccessProfileTabs
    ? [
        {
          id: "dados",
          label: "Dados",
          icon: <RiFileList3Line size={24} />,
          route: "/profile/dados",
          locked: false,
        },
        {
          id: "locator",
          label: "Localizador",
          icon: <RiCompass3Line size={24} />,
          route: "/profile/locator",
          locked: false,
        },
        ...(isFixiterForCertificate && hasLinkedCourse
          ? [
              {
                id: "certificado",
                label: "Certificado",
                icon: <RiAwardLine size={24} />,
                route: "/profile/certificado",
                locked: false,
              },
            ]
          : []),
        {
          id: "assinatura",
          label: "Assinatura",
          icon: <LuFileSignature size={24} />,
          route: "/profile/assinatura",
          locked: false,
        },
        {
          id: "usuarios",
          label: "Usuários",
          icon: <FiUsers size={24} />,
          route: "/profile/usuarios",
          locked: false,
        },
        {
          id: "impressoras",
          label: "Impressoras 3D",
          icon: <RiPrinterLine size={24} />,
          route: "/profile/impressoras",
          locked: Boolean(isFixiter),
        },
      ]
    : [];

  const isProfileTabActive = (tabId: string) => {
    const path = location.pathname.toLowerCase();
    if (!path.includes("/profile")) return false;
    if (tabId === "dados") {
      return (
        path.endsWith("/profile") ||
        path.endsWith("/profile/") ||
        path.endsWith("/my_profile") ||
        path.endsWith("/dados")
      );
    }
    return path.endsWith(`/${tabId}`);
  };

  const handleProfileHeaderClick = () => {
    if (canAccessProfileTabs) {
      setIsProfileOpen(false);
      navigate("/profile/dados");
    }
  };

  const deviceId = localStorage.getItem("@FixitApp:Device");
  const displayUserName = user?.person ? user.person.name : user.name;
  const displayUserSubtext = licensee
    ? getLicenseeDisplayName(licensee)
    : user.role;

  return (
    <Container>
      <ChangePasswordPopup
        user_id={user.id}
        setOpen={togglePopup}
        open={isPopupOpen}
      />
      {licensee && isCreatePersonOpen && (
        <Modal open={isCreatePersonOpen}>
          <PersonModal
            licensee={licensee}
            closeModal={() => {
              setisCreatePersonOpen(false);
            }}
            setPerson={setPerson}
            withoutStep
            onCreate={() => {
              window.location.reload();
            }}
          />
        </Modal>
      )}
      <NavigationAction>
        {!isSubscriptionCancelled && (
          <NavBtn toggleMenu={toggleMenu} isMenuOpen={isMenuOpen} />
        )}
        
        <LogoDesktop 
          src={Logo} 
          alt="Fix it" 
          onClick={() => navigate('/')}
        />
        <LogoMobile 
          src={Symbol} 
          alt="Fix it" 
          onClick={() => navigate('/')}
        />
      </NavigationAction>
      <TopBarOptions>
        {!isSubscriptionCancelled && (
          <>
            {user.role === roles.licensee && !isFixiter && (
              <CreditsWrapper onClick={() => navigate('/financial/transactions', { state: { openFixcoinModal: true } })}>
                <RiMoneyDollarCircleFill 
                   size={26} 
                   color="var(--fixit)" 
                   style={{ marginLeft: "-1px", zIndex: 2 }} 
                />
                <CreditsText>
                  <b>
                    {licenseeFixcoins.toLocaleString("pt-BR", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </b>{" "}
                  <span>FixCoins</span>
                </CreditsText>
                <RiAddLine size={18} color="var(--fixit)" />
              </CreditsWrapper>
            )}
            {((user.role === roles.licensee && userHasPrinter) ||
              user.role === roles.creator) && <PrinterControl />}

            <NotificationsWrapper
              onMouseEnter={handleNotificationsMouseEnter}
              onMouseLeave={handleNotificationsMouseLeave}
            >
              <ButtonIcon onClick={toggleNotifications}>
                <RiNotification2Line size={22} color="var(--fixit)" />
                {unreadCount > 0 && <NotificationBadge>{unreadCount > 9 ? '9+' : unreadCount}</NotificationBadge>}
              </ButtonIcon>

              {isNotificationsOpen && (
                <NotificationsDropdownContainer>
                  <MobileNotificationsHeader>
                    <MobileHeaderLeft>
                      <RiArrowLeftLine onClick={toggleNotifications} />
                      <MobileHeaderTitle>{`${i18n.t("topbar.notifications", "Notificações")}`}</MobileHeaderTitle>
                    </MobileHeaderLeft>
                    {unreadCount > 0 && (
                      <MarkAllReadButton onClick={handleMarkAllAsRead}>
                        {`${i18n.t("topbar.markAllRead", "Marcar todas como lidas")}`}
                      </MarkAllReadButton>
                    )}
                  </MobileNotificationsHeader>
                  <NotificationsHeader>
                    <NotificationsTitle>{`${i18n.t("topbar.notifications", "Notificações")}`}</NotificationsTitle>
                    {unreadCount > 0 && (
                      <MarkAllReadButton onClick={handleMarkAllAsRead}>
                        {`${i18n.t("topbar.markAllRead", "Marcar todas como lidas")}`}
                      </MarkAllReadButton>
                    )}
                  </NotificationsHeader>
                  <NotificationsList>
                    {isLoadingNotifications ? (
                      <EmptyNotifications>{`${i18n.t("topbar.loading", "Carregando...")}`}</EmptyNotifications>
                    ) : notifications.length === 0 ? (
                      <EmptyNotifications>{`${i18n.t("topbar.noNotifications", "Nenhuma notificação")}`}</EmptyNotifications>
                    ) : (
                      notifications.map((notif) => {
                        const styleConfig = notificationStyles[notif.type] || notificationStyles.system_alert;
                        const statusStyle = getOrderStatusStyle(notif.metadata?.status);
                        const iconColor = statusStyle?.color || styleConfig.color;
                        const iconBg = statusStyle?.backgroundColor || styleConfig.backgroundColor;

                        return (
                          <NotificationItem 
                            key={notif.id} 
                            isRead={!!notif.read_at}
                            onClick={() => handleNotificationClick(notif)}
                          >
                            <NotificationIconWrapper 
                              color={iconColor} 
                              backgroundColor={iconBg}
                            >
                              {styleConfig.icon}
                            </NotificationIconWrapper>
                            <NotificationContent>
                              <NotificationItemTitle isRead={!!notif.read_at}>
                                {notif.title}
                              </NotificationItemTitle>
                              <NotificationItemBody>
                                {notif.body}
                              </NotificationItemBody>
                              {notif.type === 'new_lead_patient' && (
                                <NotificationActionText color="#075e54">
                                  <FaWhatsapp /> Iniciar conversa no WhatsApp
                                </NotificationActionText>
                              )}
                              {(notif.type === 'order_created' || 
                                notif.type === 'order_created_urgent' || 
                                notif.type === 'order_update') && (
                                <NotificationActionText color="var(--fixit)">
                                  <RiShoppingBag3Line /> Verificar pedido
                                </NotificationActionText>
                              )}
                            </NotificationContent>
                            <NotificationTime>
                              {formatCompactTime(notif.created_at, i18n.language)}
                            </NotificationTime>
                          </NotificationItem>
                        );
                      })
                    )}
                  </NotificationsList>
                </NotificationsDropdownContainer>
              )}
            </NotificationsWrapper>
            {isNotificationsOpen && <Overlay onClick={toggleNotifications} />}
          </>
        )}

        <ProfileWrapper>
          <ProfileAvatar onClick={toggleProfile}>
            {!!user?.person
              ? user.person.name.substring(0, 2).toUpperCase()
              : user.name.substring(0, 2).toUpperCase()}
          </ProfileAvatar>

          <ProfileMenuContainer $isOpen={isProfileOpen}>
            <ProfileMenuHeader
              $clickable={canAccessProfileTabs}
              onClick={handleProfileHeaderClick}
            >
              <ProfileHeaderTextGroup>
                <ProfileHeaderPrimaryText>
                  {displayUserName}
                </ProfileHeaderPrimaryText>
                <ProfileHeaderSecondaryText>
                  {displayUserSubtext}
                </ProfileHeaderSecondaryText>
              </ProfileHeaderTextGroup>
              <NotificationIconWrapper
                color="var(--fixit)"
                backgroundColor="rgba(105, 50, 128, 0.08)"
              >
                <RiUser2Fill size={20} />
              </NotificationIconWrapper>
            </ProfileMenuHeader>

            <ProfileMenuBody>
              {profileTabOptions.map((tab) => {
                const active = isProfileTabActive(tab.id);
                return (
                  <ProfileMenuButton
                    key={tab.id}
                    $isActive={active}
                    $locked={tab.locked}
                    onClick={() => {
                      if (tab.locked) {
                        setLockedPrinterModalOpen(true);
                        return;
                      }
                      setIsProfileOpen(false);
                      navigate(tab.route);
                    }}
                  >
                    <div className="content">
                      {tab.icon}
                      <span>{tab.label}</span>
                    </div>
                    {tab.locked && (
                      <div className="lock-badge">
                        <LockedFeatureIcon
                          size={28}
                          iconSize={14}
                          open={lockedPrinterModalOpen}
                          onOpenChange={setLockedPrinterModalOpen}
                        />
                      </div>
                    )}
                  </ProfileMenuButton>
                );
              })}

              {!isSubscriptionCancelled && !isLicenseeOrFixiter && (
                <ProfileMenuButton
                  onClick={() => {
                    setIsProfileOpen(false);
                    togglePopup();
                  }}
                >
                  <div className="content">
                    <RiKey2Line size={24} />
                    <span>{`${i18n.t("topbar.change_password")}`}</span>
                  </div>
                </ProfileMenuButton>
              )}

              {!isSubscriptionCancelled && !isLicenseeOrFixiter && !user.person && (
                <ProfileMenuButton
                  onClick={() => {
                    setIsProfileOpen(false);
                    setisCreatePersonOpen(true);
                  }}
                >
                  <div className="content">
                    <HiPencilAlt size={24} />
                    <span>{`${i18n.t("topbar.addPerson")}`}</span>
                  </div>
                </ProfileMenuButton>
              )}
            </ProfileMenuBody>

            <ProfileMenuFooter>
              {deviceId && (
                <ProfileMenuButton style={{ cursor: "default" }}>
                  <div className="content">
                    <MdDevices size={24} />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>
                        Device
                      </span>
                      <span
                        style={{
                          fontSize: "11px",
                          color: "var(--gray)",
                          fontWeight: 400,
                        }}
                      >
                        {`ID: ${deviceId}`}
                      </span>
                    </div>
                  </div>
                </ProfileMenuButton>
              )}

              <ProfileMenuButton
                onClick={() => {
                  setIsProfileOpen(false);
                  signOut();
                }}
              >
                <div className="content">
                  <RiLogoutCircleRLine size={24} color="var(--error)" />
                  <span style={{ color: "var(--error)" }}>
                    {`${i18n.t("topbar.signout")}`}
                  </span>
                </div>
              </ProfileMenuButton>
            </ProfileMenuFooter>
          </ProfileMenuContainer>

          {isProfileOpen && <Overlay onClick={toggleProfile} />}
        </ProfileWrapper>
      </TopBarOptions>
    </Container>
  );
};

export default Topbar;
